<script setup lang="ts">
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {ConsumerProduct} from "@/consumer/interfaces";
import {ref, watch} from "vue";
import InteractionsView from "@/drug-interaction/components/alerts/InteractionsView.vue";
import ProfileView from "@/drug-interaction/components/alerts/ProfileView.vue";
import PriscaView from "@/drug-interaction/components/alerts/PriscaView.vue";
import HTMLInteractionView from "@/drug-interaction/components/alerts/HTMLInteractionView.vue";
import {CheckCircle} from "@lucide/vue";

defineProps<{
    products: ConsumerProduct[];
    customerId: number;
}>();

defineEmits<{
    (e: 'saved');
}>();

type Step = "profile" | "alerts";
type AlertsTab = "analysis" | "prisca" | "html";

const step = ref<Step>("profile");
const alertsTab = ref<AlertsTab>("analysis");
const open = defineModel<boolean>("open", {required: true});

watch(open, (value) => {
    if (!value) {
        step.value = "profile";
        alertsTab.value = "analysis";
    }
});

const steps: { key: Step; label: string }[] = [
    {key: "profile", label: "Profil patient"},
    {key: "alerts", label: "Interactions"},
];

const alertsTabs: { key: AlertsTab; label: string }[] = [
    {key: "analysis", label: "Analyse"},
    {key: "prisca", label: "Graphique des interactions"},
    {key: "html", label: "Détail Vidal"},
];

const stepIndex = (key: Step) => steps.findIndex(s => s.key === key);
const currentIndex = () => stepIndex(step.value);
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="h-[85vh] min-w-[75vw] flex flex-col gap-0 p-0 overflow-hidden">
      <div class="px-6 pt-5 pb-4 border-b shrink-0">
        <DialogHeader>
          <DialogTitle class="text-base font-semibold">
            Analyse des interactions médicamenteuses
          </DialogTitle>
        </DialogHeader>

        <div class="flex items-center gap-2 mt-4">
          <template v-for="(_step, index) in steps" :key="_step.key">
            <div class="flex items-center gap-1.5">
              <div
                class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors"
                :class="index <= currentIndex() ? 'bg-primary text-white' : 'bg-gray-200 text-gray-400'"
              >
                <CheckCircle v-if="index < currentIndex()" class="w-4 h-4" />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="text-xs font-medium transition-colors"
                :class="index === currentIndex() ? 'text-primary' : index < currentIndex() ? 'text-gray-500' : 'text-gray-400'"
              >
                {{ _step.label }}
              </span>
            </div>
            <div v-if="index < steps.length - 1" class="flex-1 h-px bg-gray-200 max-w-8" />
          </template>
        </div>

        <div v-if="step === 'alerts'" class="flex gap-1 mt-4">
          <button
            v-for="tab in alertsTabs"
            :key="tab.key"
            class="px-3 py-1 text-xs rounded-md font-medium transition-colors"
            :class="alertsTab === tab.key
              ? 'bg-primary text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'"
            @click="alertsTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div
        class="flex-1 min-h-0 overflow-hidden flex flex-col"
        :class="alertsTab !== 'analysis' ? '' : 'px-6 py-4'"
      >
        <ProfileView
          v-if="step === 'profile'"
          :customer-id="customerId"
          @next="step = 'alerts'"
        />

        <template v-else-if="step === 'alerts'">
          <InteractionsView
            v-show="alertsTab === 'analysis'"
            :customer-id="customerId"
            :products="products"
            @previous="step = 'profile'"
            @saved="$emit('saved')"
          />
          <PriscaView
            v-if="alertsTab === 'prisca'"
            :customer-id="customerId"
            :products="products"
            class="flex-1 px-6 py-4"
            @next="alertsTab = 'html'"
          />
          <HTMLInteractionView
            v-if="alertsTab === 'html'"
            :customer-id="customerId"
            :products="products"
            class="flex-1 px-6 py-4"
            @next="alertsTab = 'analysis'"
          />
        </template>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
</style>
